import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import { ReactComponent as FilterAltOutlinedIcon } from '../../../static/icons/filter.svg';
import { Badge, Box, Card, Chip, Grid, Stack } from '@mui/material';
import * as React from 'react';
import { getFilters, getSkillSummary, getWidgetConfig } from '../../../services/api-service';
import { WIDGET_REFRESH_INTERVAL } from '../../../utils/constants';
import FilterDrawer from '../../FilterDrawer/FilterDrawer';
import OZTable from '../../OZTable/OZTable';
import WidgetDownload from '../../Widget-Download/Widget-Download';
import WidgetGraph from '../../Widget-Graph/Widget-Graph';
import '../SkillSummary/SkillSummary.css';
import ThresholdDrawer from '../../ThresholdDrawer/ThresholdDrawer';
import { checkDataAccessFilters, determineFilterType, getTotalRowAndActualData, mapRowDataWithThreshold, mapRowDataWithThresholdNew, secondsToMinSec } from '../../../utils/util';
import { getLastFetchedDetailFromBrowser } from '../../../services/application-service';

const defaultFilters = [
	{ id: 1, label: 'General', color: '#EE4060' },
	{ id: 2, label: 'Canada Sales', color: '#EEBE40' },
	{ id: 3, label: 'Support', color: '#B0CFA5' },
	{ id: 4, label: 'English Sales', color: '#53B9ED' },
	{ id: 5, label: 'Premium Plus', color: '#B2A1F9' }
];

const thresholdFields = [
	{ label: 'Longest call in Queue', value: '', key: 'LongestCallQueueTime', condition: 'GREATER_THAN' },
	{ label: 'Abandoned', value: '', key: 'Abandoned', condition: 'GREATER_THAN' },
	{ label: 'SLA', value: '', key: 'SLAPercentage', condition: 'GREATER_THAN' },
	{ label: 'ASA', value: '', key: 'AverageSpeedToAnswer', condition: 'GREATER_THAN' },
	{ label: 'AHT', value: '', key: 'AverageHandlingTime', condition: 'GREATER_THAN' }
];


function SkillSummary() {
	const { useState, useRef } = React;
	const downloadContent = useRef<null | HTMLElement>(null);
	const [responseLength, setresponseLength] = useState<Number>(0);
	const [isDrawerOpen, setIsDrawerOpen] = useState<boolean | undefined>(false);
	const [rowData, setRowData] = useState<Array<any>>([]);
	const [columnData, setColumnData] = useState<Array<any>>([]);
	const [skillSummaryFilter, setSkillSummaryFilter] = useState<any>([]);
	const [filterType, setFilterType] = useState<any>('1');
	const [isThresholdDrawerOpen, setIsThresholdDrawerOpen] = useState<boolean | undefined>(false);
	const [thresholdItems, setThresholdItems] = useState<Array<any>>([]);
	const [allowedFilters, setAllowedFilters] = useState<string[]>(['Campaign', 'Skill', 'Group']);
	const [lastFetchedTime, setLastFetchedTime] = useState<any>('');
	const intervalRef = useRef<any>(null);
	const clearFilter = () => {
		setSkillSummaryFilter([]);
	};

	const handleFilterDelete = (id: Number) => {
		const newFilter = skillSummaryFilter.filter((item: any) => item.id !== id);
		setSkillSummaryFilter(newFilter);
	};

	const filterItems = skillSummaryFilter.map((item: any, index: number) => (
		<Chip key={index} label={item.label} color="primary" variant="outlined" onDelete={() => handleFilterDelete(item.id)} deleteIcon={<CloseOutlinedIcon style={{ fontSize: '12px' }} />} />
	));

	const retrieveTableData = async (filters = '0') => {
		try {
			const rows: any[] = await getSkillSummary({ filters, type: filterType });
			const { totalRow, actualData } = getTotalRowAndActualData(rows, 'SkillName');
			const columns = [
				{ name: 'Skills', key: 'SkillName', subTitle: actualData.length },
				{ name: 'Total Calls', key: 'TotalCalls', subTitle: totalRow?.TotalCalls ?? 0, minWidth: 120 },
				{ name: 'Total Agents', key: 'TotalAgents', subTitle: totalRow?.TotalAgents ?? 0, minWidth: 120 },
				{ name: 'Calls Per Agents', key: 'CallsPerAgent', subTitle: totalRow?.CallsPerAgent ?? 0, minWidth: 170 },
				{ name: 'Longest Call in Queue', key: 'LongestCallQueueTime', subTitle: totalRow?.LongestCallQueueTime ?? 0, type: 'MIN_SEC', minWidth: 180 },
				{ name: 'Answered', key: 'Answered', subTitle: totalRow?.Answered ?? 0 },
				{ name: 'Abandoned', key: 'Abandoned', subTitle: totalRow?.Abandoned ?? 0 },
				{ name: 'SLA %', key: 'SLAPercentage', subTitle: totalRow?.SLAPercentage ?? 0 },
				{ name: 'ASA', key: 'AverageSpeedToAnswer', subTitle: totalRow?.AverageSpeedToAnswer ?? 0, type: 'MIN_SEC' },
				{ name: 'AHT', key: 'AverageHandlingTime', subTitle: totalRow?.AverageHandlingTime ?? 0, type: 'MIN_SEC' }
			];
			// const updatedRowData = mapRowDataWithThreshold(thresholdItems, actualData);
			const updatedRowData = mapRowDataWithThresholdNew(thresholdItems, actualData);
			setRowData(updatedRowData);
			setColumnData(columns);
			setLastFetchedTime(getLastFetchedDetailFromBrowser());
		}
		catch (error) {
			console.error('Error fetching skill summary:', error);
		}
	};

	const toggleDrawer = (isOpen: boolean) => {
		setIsDrawerOpen(isOpen);
	};

	const handleDownload = (type: string) => {
		console.log(type);
	};

	const updateThresholdItems = async () => {
		const newThresholdItems = [];
		const response: any = await getFilters('Skill');
		newThresholdItems.push({ label: 'Organization Level', fields: thresholdFields, isOrgSkill: true });
		for (const row of response) {
			if (row.name !== 'All') {
				newThresholdItems.push({ label: row.Name, key: 'SkillName', value: row.Name, fields: thresholdFields });
			}
		}
		setThresholdItems(newThresholdItems);
	};

	React.useEffect(() => {
		const fetchWidgetConfig = async () => {
			try {
				if (checkDataAccessFilters()) {
					let widgetConfig = await getWidgetConfig('109', 'widget');
					if (widgetConfig) {
						if (typeof widgetConfig === 'string') {
							widgetConfig = JSON.parse(widgetConfig);
						}
						// Check the condition and set the appropriate filters
						const filters = widgetConfig.data && checkDataAccessFilters()
							? widgetConfig.data
							: widgetConfig.config;

						if (filters) {
							const filtersArray = filters.split(',');
							console.log("here filters arra", filtersArray)
							setAllowedFilters(filtersArray);
							const determinedFilterType = determineFilterType(filtersArray);
							if (determinedFilterType !== undefined) {
								setFilterType(determinedFilterType);
							}
						}
					}
				}
			} catch (error) {
				console.error('Error fetching widget config:', error);
			}
		};
		fetchWidgetConfig();
	}, []);

	React.useEffect(() => {
		updateThresholdItems();
		return () => {
			clearInterval(intervalRef.current);
		};
	}, []);

	React.useEffect(() => {
		console.log("responseLength", responseLength);
		const filterIds = (skillSummaryFilter.length && !(responseLength === skillSummaryFilter.length)) ? skillSummaryFilter.map((item: any) => item.id).join(',') : '0';
		// const filterIds = skillSummaryFilter.length ? skillSummaryFilter.map((item: any) => item.id).join(',') : '0';
		retrieveTableData(filterIds);
		if (intervalRef.current) {
			clearInterval(intervalRef.current);
		}
		intervalRef.current = setInterval(() => retrieveTableData(filterIds), WIDGET_REFRESH_INTERVAL.SKILL_SUMMARY);
	}, [skillSummaryFilter, thresholdItems]);

	const handleApplyFilters = ({ selectedFilters, filterType: newFilterType }: any) => {
		setSkillSummaryFilter(selectedFilters);
		setFilterType(newFilterType);
		toggleDrawer(false);
	};

	const toggleThresholdDrawer = (isOpen: boolean, thresholdData: any) => {
		setIsThresholdDrawerOpen(isOpen);
		if (thresholdData) {
			setThresholdItems(thresholdData);
		}
	};

	function getRowData(row: any) {
		row = row?.map((item: any) => {
			let rowObj = {
				...item,
				AverageHandlingTime: secondsToMinSec(item?.AverageHandlingTime),
				AverageSpeedToAnswer: secondsToMinSec(item?.AverageSpeedToAnswer),
				LongestCallQueueTime: secondsToMinSec(item?.LongestCallQueueTime),
			};
			return rowObj;
		});
		return row;
	}

	return (
		<>
			<Card className="customCard">
				<div className="card-header">
					<div className="card-title">
						Skill Summary
						<span className="card-title-badge">Live</span>
						<span className="last-fetched-time" style={{ marginLeft: '6px' }}>Last updated at {lastFetchedTime} </span>
					</div>
					<div className="card-actions">
						<WidgetDownload element={downloadContent} handleDownload={handleDownload} xlsx rowData={getRowData(rowData)} columns={columnData} name={'Skill Summary'} ></WidgetDownload>
						<div className="card-icon" onClick={() => toggleDrawer(true)}>
							<Badge badgeContent={skillSummaryFilter.length} color="primary">
								<FilterAltOutlinedIcon fontSize="small" style={{ color: '#536580' }} />
							</Badge>
						</div>
						<div onClick={() => toggleThresholdDrawer(true, null)}>
							<WidgetGraph></WidgetGraph>
						</div>
					</div>
				</div>
				<Box sx={{ width: '100%', typography: 'body1' }}>
					{skillSummaryFilter.length > 0 && (
						<Box className="customTabPanel">
							<Grid container spacing={2}>
								<Grid item xs={12}>
									<div className="filter-outer">
										<div className="filter-container">
											<Stack direction="row" spacing={1}>
												{filterItems}
											</Stack>
										</div>
										<div className="filter-remove" onClick={clearFilter}>
											Remove
										</div>
									</div>
								</Grid>
							</Grid>
						</Box>
					)}
					<OZTable downloadContent={downloadContent} columns={columnData} rows={rowData}></OZTable>
				</Box>
			</Card>
			<FilterDrawer isOpen={isDrawerOpen} toggleDrawer={toggleDrawer} allowedFilters={allowedFilters} applyFilters={handleApplyFilters} existingSelectedFilters={skillSummaryFilter} additionalApiParams={{ campaignType: 'inbound' }} setResponseLength={setresponseLength} />
			<ThresholdDrawer isOpen={isThresholdDrawerOpen} title={'Skill Summary'} toggleDrawer={toggleThresholdDrawer} thresholdItems={thresholdItems} />
		</>
	);
}
export default SkillSummary;
